import { v4 as uuidv4 } from 'uuid';
import filesApi, { FileMetadata } from '@/services/apis/files';
import { FILE_TYPE_CATEGORIES, getMimeTypeFromExtension } from '@/components/base/file-upload/utils';

export type UploadStage = 'getting-url' | 'uploading' | 'saving-metadata';

export interface UploadFileToS3Options {
  file: File | Blob;
  filename: string;
  maxSizeMb?: number;
  isMediaLibrary?: boolean;
  allowedMimeTypes?: string[];
  onProgress?: (percent: number) => void;
  onStageChange?: (stage: UploadStage) => void;
}

export const putFileToPresignedUrl = (
  file: File | Blob,
  presignedUrl: string,
  onProgress?: (percent: number) => void,
): Promise<void> => {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();

    xhr.upload.addEventListener('progress', (event) => {
      if (event.lengthComputable && onProgress) {
        const progress = Math.round((event.loaded / event.total) * 100);
        onProgress(Math.round((progress * 80) / 100));
      }
    });

    xhr.addEventListener('load', () => {
      if (xhr.status === 200 || xhr.status === 204) {
        resolve();
      } else {
        const errorMessage =
          xhr.status === 0
            ? 'CORS error: Check S3 bucket CORS configuration. The bucket must allow your origin and PUT method.'
            : `Upload failed with status ${xhr.status}`;
        reject(new Error(errorMessage));
      }
    });

    xhr.addEventListener('error', () => {
      if (xhr.status === 0 && xhr.readyState === 4) {
        reject(
          new Error(
            'CORS error: The S3 bucket must have CORS configuration allowing your origin. Please check your S3 bucket CORS settings.',
          ),
        );
      } else {
        reject(new Error('Upload failed'));
      }
    });

    xhr.addEventListener('abort', () => {
      reject(new Error('Upload aborted'));
    });

    xhr.open('PUT', presignedUrl);
    xhr.send(file);
  });
};

export const uploadFileToS3 = async ({
  file,
  filename,
  maxSizeMb = 10,
  isMediaLibrary = false,
  allowedMimeTypes,
  onProgress,
  onStageChange,
}: UploadFileToS3Options): Promise<FileMetadata> => {
  const fileSize = file.size;

  if (fileSize > maxSizeMb * 1024 * 1024) {
    throw new Error(`File size exceeds ${maxSizeMb}MB limit`);
  }

  const mimeType = getMimeTypeFromExtension(filename) || file.type || 'application/octet-stream';

  if (allowedMimeTypes && !allowedMimeTypes.includes(mimeType)) {
    throw new Error(`File type ${mimeType} is not allowed`);
  }

  onStageChange?.('getting-url');
  onProgress?.(0);

  const objectKey = `uploads/${uuidv4()}-${filename}`;

  const presignedUrlResponse = await filesApi.getPresignedUrl({
    objectKey,
    httpMethod: 'PUT',
    contentType: mimeType,
    expiresIn: 300,
  });

  onStageChange?.('uploading');
  onProgress?.(0);

  await putFileToPresignedUrl(file, presignedUrlResponse.presignedUrl, onProgress);

  onStageChange?.('saving-metadata');
  onProgress?.(90);

  const fileMetadata = await filesApi.saveFileMetadata({
    name: filename,
    url: presignedUrlResponse.fileUrl,
    size: fileSize,
    type: mimeType,
    provider: 's3',
    isMediaLibrary,
  });

  onProgress?.(100);

  return fileMetadata;
};

export const IMAGE_MIME_TYPES = [...FILE_TYPE_CATEGORIES.image.mimeTypes];
